import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clover, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/lib/api';

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const next = params.get('next') || '/dashboard';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await register(email, password, name);
      toast.success('Account created');
      nav(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Registration failed');
    } finally { setBusy(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bg-grain relative min-h-screen text-white">
      <Navbar />
      <div className="orb" style={{ top: '10%', right: '20%', width: 380, height: 380, background: 'radial-gradient(circle, rgb(var(--brand-rgb)/0.45), transparent 70%)' }} />

      <div className="max-w-md mx-auto pt-32 pb-20 px-6 relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-center mb-10">
            <Clover className="h-8 w-8 text-brand mx-auto drop-shadow-[0_0_14px_rgb(var(--brand-rgb)/0.7)]" />
            <h1 className="mt-4 font-display font-bold text-4xl tracking-tight">Create your account</h1>
            <p className="text-white/50 mt-2">Instant access. No spam. Ever.</p>
          </div>

          <div className="glass-strong rounded-2xl p-8">
            <button
              data-testid="google-signup-btn"
              onClick={startGoogle}
              className="w-full inline-flex items-center justify-center gap-3 py-3 rounded-full bg-white text-neutral-900 font-medium hover:bg-white/90 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Sign up with Google
            </button>
            <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-white/40">
              <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/50">Name</span>
                <div className="mt-1 relative">
                  <User className="h-4 w-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="register-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand outline-none transition-colors text-white placeholder-white/30"
                    placeholder="Your gamertag"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/50">Email</span>
                <div className="mt-1 relative">
                  <Mail className="h-4 w-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="register-email"
                    type="email" required autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand outline-none transition-colors text-white placeholder-white/30"
                    placeholder="you@example.com"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/50">Password</span>
                <div className="mt-1 relative">
                  <Lock className="h-4 w-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="register-password"
                    type="password" required minLength={6} autoComplete="new-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand outline-none transition-colors text-white placeholder-white/30"
                    placeholder="Min 6 characters"
                  />
                </div>
              </label>
              {error && <div data-testid="register-error" className="text-red-400 text-sm">{error}</div>}
              <button
                data-testid="register-submit"
                type="submit" disabled={busy}
                className="w-full py-3 rounded-full bg-brand text-[#0a0a0a] font-semibold disabled:opacity-50 hover:shadow-[0_0_30px_rgb(var(--brand-rgb)/0.6)] transition-shadow"
              >
                {busy ? 'Creating…' : 'Create account'}
              </button>
            </form>

            <p className="text-center text-sm text-white/50 mt-6">
              Already have an account?{' '}
              <Link data-testid="go-login" to="/login" className="text-brand hover:underline">Log in</Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
